// src/domain/oura-sanity.ts
// Pure TypeScript sanity-check functions for Oura Ring data validation.
// No I/O, no side effects — fully unit-testable.
// Mirrored (without runOuraSanityTests) in supabase/functions/_shared/oura-sanity.ts for Deno.

export type SanityResult = { ok: true } | { ok: false; reason: string };

/**
 * Validates that activeCalories from Oura is physiologically plausible.
 *
 * Rules:
 *   < 0    → always reject
 *   > 4500 → hard reject (extreme outlier / sensor error)
 *   0–4500 → accepted
 */
export function checkActiveCaloriesSanity(activeCalories: number): SanityResult {
  if (!Number.isFinite(activeCalories) || activeCalories < 0) {
    return { ok: false, reason: "negative_active_calories" };
  }
  if (activeCalories > 4500) {
    return { ok: false, reason: "active_calories_exceeds_hard_limit_4500" };
  }
  return { ok: true };
}

/**
 * Validates that totalBurned (total_calories from Oura) is physiologically plausible
 * relative to the user's BMR.
 *
 * Rules:
 *   min = max(1200, 0.9 * BMR)   — covers very sedentary days, floors at 1200
 *   max = min(6500, 3.5 * BMR)   — covers extreme activity, caps at 6500
 *   cross-check: totalBurned must be >= activeCalories + 0.6*BMR when active > 0
 */
export function checkTotalBurnedSanity(
  totalBurned: number,
  bmr: number,
  activeCalories?: number | null,
): SanityResult {
  if (!Number.isFinite(bmr) || bmr <= 0) {
    return { ok: false, reason: "invalid_bmr" };
  }
  if (!Number.isFinite(totalBurned) || totalBurned <= 0) {
    return { ok: false, reason: "total_burned_not_positive" };
  }

  const min = Math.max(1200, 0.9 * bmr);
  const max = Math.min(6500, 3.5 * bmr);

  if (totalBurned < min) {
    return { ok: false, reason: `total_burned_below_min:${Math.round(min)}` };
  }
  if (totalBurned > max) {
    return { ok: false, reason: `total_burned_above_max:${Math.round(max)}` };
  }

  // Cross-check: totalBurned must be >= active + 60% of BMR (minimum resting burn)
  if (activeCalories != null && Number.isFinite(activeCalories) && activeCalories > 0) {
    const impliedMin = activeCalories + bmr * 0.6;
    if (totalBurned < impliedMin) {
      return {
        ok: false,
        reason: `total_burned_less_than_active_plus_0.6_bmr:${Math.round(impliedMin)}`,
      };
    }
  }

  return { ok: true };
}

/**
 * Inline self-tests for the sanity check functions.
 * Called from WeightTracker.tsx runSelfTests() in development.
 */
export function runOuraSanityTests(): void {
  const ok   = (r: SanityResult, label: string) => console.assert(r.ok === true,  `[oura-sanity] FAIL: ${label}`);
  const fail = (r: SanityResult, label: string) => console.assert(r.ok === false, `[oura-sanity] FAIL: ${label}`);

  // checkActiveCaloriesSanity
  ok(checkActiveCaloriesSanity(0),    "active 0 (rest day)");
  ok(checkActiveCaloriesSanity(500),  "active 500 (normal)");
  ok(checkActiveCaloriesSanity(4500), "active 4500 (at hard limit)");
  fail(checkActiveCaloriesSanity(4501), "active 4501 (over hard limit)");
  fail(checkActiveCaloriesSanity(-1),   "active negative");

  // checkTotalBurnedSanity — BMR=1800: min=max(1200,1620)=1620, max=min(6500,6300)=6300
  ok(checkTotalBurnedSanity(2200, 1800),         "total 2200 bmr=1800");
  ok(checkTotalBurnedSanity(1620, 1800),         "total at min boundary (1620)");
  fail(checkTotalBurnedSanity(1619, 1800),       "total below min (1619)");
  ok(checkTotalBurnedSanity(6300, 1800),         "total at max boundary (6300)");
  fail(checkTotalBurnedSanity(6301, 1800),       "total above max (6301)");

  // Absolute floor: BMR=1000 → min=max(1200,900)=1200
  ok(checkTotalBurnedSanity(1250, 1000),         "total above absolute floor with low BMR");
  fail(checkTotalBurnedSanity(1100, 1000),       "total below absolute floor 1200");

  // Absolute ceiling: BMR=2500 → max=min(6500,8750)=6500
  ok(checkTotalBurnedSanity(6500, 2500),         "total at absolute ceiling (6500)");
  fail(checkTotalBurnedSanity(6501, 2500),       "total above absolute ceiling");

  // Cross-check: active=800, bmr=1800 → impliedMin=800+1080=1880
  ok(checkTotalBurnedSanity(1880, 1800, 800),    "cross-check at exact implied min");
  fail(checkTotalBurnedSanity(1879, 1800, 800),  "cross-check below implied min");

  // Invalid BMR
  fail(checkTotalBurnedSanity(2000, 0),          "zero BMR rejected");
  fail(checkTotalBurnedSanity(2000, -100),       "negative BMR rejected");

  if (import.meta.env.DEV) console.log("[oura-sanity] All sanity tests passed.");
}
